import type { RosterPlayerFormValues } from '../../../team/player/types'

/** The cells the Operations roster grid allows editing in place. */
export type EditablePlayerField = 'name' | 'jersey' | 'position' | 'email'

/**
 * Split a typed full name into first/last. Everything after the first space is
 * the surname, so "Van Der Berg" survives as a single last name rather than
 * being truncated.
 */
export const splitFullName = (
  value: string
): { firstName: string; lastName: string } => {
  const parts = value.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) {
    return { firstName: '', lastName: '' }
  }
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  }
}

/**
 * Apply one inline cell edit to the player's form values.
 *
 * Returns a NEW object; the caller keeps the original as the `initial` side of
 * `toRosterUpdatePayload`, which diffs the two and sends only what changed. That
 * is what keeps a single-cell save from touching any other field.
 */
export const applyInlineEdit = (
  values: RosterPlayerFormValues,
  field: EditablePlayerField,
  rawValue: string
): RosterPlayerFormValues => {
  const value = (rawValue ?? '').trim()

  switch (field) {
    case 'name': {
      const { firstName, lastName } = splitFullName(value)
      return { ...values, firstName, lastName }
    }
    case 'jersey':
      return { ...values, playerNumber: value }
    case 'position':
      return { ...values, positionId: value }
    case 'email':
      return { ...values, email: value }
    default:
      return values
  }
}

/**
 * Whether an edit is worth sending. A no-op edit (unchanged, or a name cleared
 * to nothing) must not reach the server — AG Grid commits on blur, so simply
 * tabbing through a cell would otherwise fire a PUT.
 */
export const isMeaningfulEdit = (
  field: EditablePlayerField,
  rawValue: string,
  currentValue: string
): boolean => {
  const value = (rawValue ?? '').trim()
  if (value === (currentValue ?? '').trim()) {
    return false
  }
  // A player must keep a name; clearing the cell is a mistake, not an edit.
  if (field === 'name' && value === '') {
    return false
  }
  return true
}
